import React, {Component} from 'react'
import { connect } from "react-redux"
import trans from "trans"

import {
    css,
    Select,
    PopupHint,
    Grid,
    GridCell,
    Radio,
    Icon,
    Toggler,
    TextInput,
    FieldLabel,
} from "@bdfx/mergado-ui-kit"

import { updateInputValue as change } from "app/reducers/form"
import {NumericInput} from "shape-edit/forms/fields";
import { iconDisabledColor } from "./tools-panel";
import { canBeOutpaintedOver, getElementValue, getFormName, getSelectedAltLevels, getTemplateSize } from 'shape-edit/forms/helpers'
import { RandomProduct } from 'shape-edit/types'
import { selectFormValues } from 'app/reducers/form'
import { RENDERED_TYPE_OUTPAINTING } from 'products/types'
import { UpdateEffectsFunctionType } from 'template-edit/containers/Editor'
import { TemplateOrAlternativeTemplateType } from 'template-edit/undoRedoReducers'
import { Project, Template } from 'app/types/model-types'

export interface Props {
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    templateForm: Template
    project: Project
    randomProduct: RandomProduct
    change: typeof change
    showOverlap: boolean
    setShowOverlap: Function
    updateEffects: UpdateEffectsFunctionType
}

const { abs, sin, cos, PI, round, acos, sqrt, pow, min, max, floor, ceil } = Math

class EnlargeByAndOverscan extends Component<Props> {
    handleToggleOverscanVsSize = (value) => {
        const { form, change, isAlt } = this.props
        const templateSize = getTemplateSize()
        const formName = getFormName(isAlt)

        const screenWidth = templateSize.width
        const screenHeight = templateSize.height

        if (value) {
            const overscan = form.max_image_width && form.max_image_height ?
                .5 * (max(form.max_image_width - screenWidth, form.max_image_height - screenHeight)) : 0
            change(formName, 'max_image_width', null)
            change(formName, 'max_image_height', null)
            change(formName, 'overscan', overscan)
        } else {
            change(formName, 'max_image_width', screenWidth + 2*form.overscan)
            change(formName, 'max_image_height', screenHeight + 2*form.overscan)
            change(formName, 'overscan', 0)
        }
    }

    setAutocrop = (val) => {
        const { form, isAlt } = this.props
        const formName = getFormName(isAlt)

        this.props.change(formName, 'is_autocrop', val)
        if (this.props.randomProduct?.id) {
            const a = getSelectedAltLevels()
            this.props.updateEffects(this.props.randomProduct.id, form.effects, val, a?.levels, a?.mode)
        }
    }

    render() {
        const { form, isAlt, change } = this.props

        const templateSize = getTemplateSize()
        const formName = getFormName(isAlt)

        const byOverscan = !(form.max_image_width && form.max_image_height)

        const s = <span className='clickable' title={trans(byOverscan ? 'by_max_size' : 'by_overscan')} onClick={() => this.handleToggleOverscanVsSize(!byOverscan)}>
            <Icon type="ellypsis-vertical-light" color={iconDisabledColor} />
        </span>

        return (
            <StyledGrid>
                <Select
                    label={trans('enlarge_by')}
                    value={form.enlarge_by}
                    placeholder={trans('no_enlarge_nor_crop')}
                    options={[
                        { value: 'fit', label: trans("by_fit") },
                        { value: 'bigger', label: trans("by_bigger") },
                        { value: 'smaller', label: trans("by_smaller") },
                        { value: 'width', label: trans("by_width") },
                        { value: 'height', label: trans("by_height") },
                        { value: 'none', label: trans("no_enlarge_nor_crop") },
                    ]}
                    onChange={(enlargeBy) => {
                        if (enlargeBy === null) {
                            change(formName, 'enlarge', 0)
                            change(formName, 'crop', 0)
                            change(formName, 'enlarge_by', enlargeBy)
                        } else {
                            change(formName, 'enlarge', 1)
                            change(formName, 'enlarge_by', enlargeBy)
                        }
                    }}
                />

                <GridCell style={{ marginTop: '20px' }} onClick={() => this.setAutocrop(!form.is_autocrop)}>
                    <FieldLabel>{trans('adjust_size')}</FieldLabel>
                    <Toggler
                        onLabel={trans('is_autocrop')}
                        value={!!form.is_autocrop}
                        onChange={(val) => this.setAutocrop(val)}
                    />
                </GridCell>

                {(!!form?.enlarge_by && form?.enlarge_by !== 'none') &&
                    <GridCell style={{ marginTop: '10px' }}>
                        {byOverscan ?
                            <Grid cols={'1fr 1fr'} gap={'20px'}>
                                <GridCell>
                                    <NumericInput
                                        formName={formName}
                                        label={<React.Fragment>
                                            {trans('overscan')} (px) &nbsp;
                                            <PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{trans("overscan_hint")}</PopupHint>&nbsp;
                                            {s}
                                        </React.Fragment>}
                                        fieldName={'overscan'}
                                        placeholder={'overscan'}
                                        id={'overscan'}
                                        units={'px'}
                                        min={(-floor(templateSize.width / 2) + 1)}
                                        max={templateSize.width}
                                        step={10}
                                        value={-round(form.overscan)}
                                        onChangeFunction={(val) => {
                                            change(formName, 'overscan', -val)
                                        }}
                                    />
                                </GridCell>
                                {byOverscan &&
                                    <GridCell style={{ alignSelf: 'center' }} onClick={() => this.props.setShowOverlap(!this.props.showOverlap)}>
                                        <Toggler
                                            onLabel={trans('disable_show_overlap')}
                                            value={!this.props.showOverlap}
                                            style={{ marginTop: '20px' }}
                                        /* onChange={(value) => this.props.setShowOverlap(!value)} */
                                        />
                                    </GridCell>
                                }
                            </Grid> :
                            <Grid cols={'1fr 1fr'} gap={'20px'}>
                                <GridCell>
                                    <NumericInput
                                        formName={formName}
                                        label={<React.Fragment>{trans('max_image_width')}{s}</React.Fragment>}
                                        fieldName={'max_image_width'}
                                        id={'max_image_width'}
                                        units={'px'}
                                        min={0}
                                        max={4 * templateSize.width}
                                        step={10}
                                        value={round(form.max_image_width)}
                                    />
                                </GridCell>
                                <GridCell>
                                    <NumericInput
                                        formName={formName}
                                        label={trans('max_image_height')}
                                        fieldName={'max_image_height'}
                                        id={'max_image_height'}
                                        units={'px'}
                                        min={0}
                                        max={4 * templateSize.height}
                                        step={10}
                                        value={round(form.max_image_height)}
                                    />
                                </GridCell>
                            </Grid>
                        }
                    </GridCell>
                }

                {!!(canBeOutpaintedOver(true, form.overscan, form.translate_x, form.translate_y) && form.type === RENDERED_TYPE_OUTPAINTING) &&
                    <GridCell>
                        <Toggler
                            onLabel={trans('outpaint_over')}
                            title={trans('outpaint_over')}
                            name={`${formName}.outpaint_over`}
                        />
                    </GridCell>
                }


            </StyledGrid>
        )
    }
}

const StyledGrid = css(Grid)({
    marginTop: '25px',
    fontSize: '14px',
    ' .placeholder': {
        color: iconDisabledColor,
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 10,
    },
    ' .shorter': {
        ' input': {
            paddingLeft: '30px',
        },
    },
    ' .alt-image-layout': {
        fill: '#eee',
    },
    ' .shadow-box': {
        ' .alt-image-layout': {
            fill: '#cce4fb',
            stroke: '#fff',
        },
    },
    ' .date-range-form': {
        /*position: 'relative',*/
    },
    ' .date-range-form-label': {
        alignSelf: 'center',
        position: 'relative',
    },
    ' .muk-datepicker': {
        position: 'unset',
    },
    ' .muk-datepicker-popover': {
        maxWidth: '100%',
        fontSize: '9px',
        overflowX: 'auto',
    },
    ' .muk-datepicker-picker': {
        border: '1px solid #ccc',
    },
    ' #flood-fill-points': {
        maxWidth: '100%',
        maxHeight: '200px',
        width: 'auto',
        height: 'auto',
        boxShadow: '0 0 3px 1px rgba(0,0,0,.3)',
        webkitBoxShadow: '0 0 3px 1px rgba(0,0,0,.3)',
        mozBoxShadow: '0 0 3px 1px rgba(0,0,0,.3)',
    },
})

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        form: selectFormValues(state, getFormName(state.isAlt)),
        templateForm: selectFormValues(state, 'templateForm'),
        randomProduct: state.randomProduct,
        project: state.project,
    }
}

export default connect(mapStateToProps, {
    change,
})(EnlargeByAndOverscan)
